import { differenceInDays, format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import {
    DG_COLOR_SHADES,
    LEVELS,
    PI_COLOR_SHADES,
    PP_COLOR_SHADES,
    TOPICS,
    TARGET_LIST_DIGITAL_VS_NON_DIGITAL,
    AFFINITY_HUB_DIGITAL_VS_NON_DIGITAL,
    DISTINCT_CHANNELS,
    DISTINCT_PARTNERS,
    colors,
    PEER_TO_PEER_AFFINITY,
    ENGAGEMENT,
    PHYSICIAN_SEGMENT,
    channelDefinitions,
    REGENERON_COMPANY_ID,
    primaryColors,
} from '../constants';

export const ordinalSuffix = (num) => {
    const j = num % 10,
        k = num % 100;
    if (j == 1 && k != 11) {
        return 'st';
    }
    if (j == 2 && k != 12) {
        return 'nd';
    }
    if (j == 3 && k != 13) {
        return 'rd';
    }
    return 'th';
};

export const getHumanDateRange = (dateRange) => {
    switch (dateRange) {
        case '7d':
            return 'week';
        case '30d':
            return 'month';
        case '3m':
            return 'three months';
        case '1y':
            return 'year';
        case '2y':
            return '2 year';
        case '3y':
            return '3 year';
    }
};

export const getDifferenceBetweenDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const daysDifference = differenceInDays(endDate, startDate);
    return daysDifference + 1 + ' days';
};

export const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2019; year--) {
        years.push(year);
    }

    return years;
};

export const generateDistinctHexColors = (number) => {
    const colors = new Set(); // Use a Set to store unique colors
    const uniqueColors = [];

    while (colors.size < number) {
        const n = (Math.random() * 0xfffff * 1000000).toString(16);
        const color = '#' + n.slice(0, 6);

        // Add the color to the Set if it's not already present
        if (!colors.has(color)) {
            colors.add(color);
            uniqueColors.push(color);
        }
    }

    return uniqueColors;
};

export const generateDistinctColors = (numColors) => {
    const colors = [];
    const colorStep = 360 / numColors; // Adjust based on the number of colors you need

    for (let i = 0; i < numColors; i++) {
        // Calculate the hue for each color
        const hue = i * colorStep;

        // Convert HSL to RGB
        const { r, g, b } = hslToRgb(hue / 360, 0.5, 0.5);

        // Convert RGB to hexadecimal color code
        const hexColor = rgbToHex(Math.floor(r), Math.floor(g), Math.floor(b));

        colors.push(hexColor);
    }

    return colors;
};

// Function to convert HSL to RGB
const hslToRgb = (h, s, l) => {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // Achromatic (grayscale)
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: r * 255,
        g: g * 255,
        b: b * 255,
    };
};

// Function to convert RGB to hexadecimal color code
const rgbToHex = (r, g, b) =>
    `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
/**
 * Finds the batch containing a specific element within a paginated dataset.
 *
 * @param {number} element - The index or position of the element in the dataset.
 * @param {number} totalSize - The total number of elements in the dataset.
 * @param {number} pageSize - The number of elements in each batch or page.
 * @returns {Object} - An object containing information about the batch:
 *   - batchNumber: The batch number to which the given element belongs.
 *   - firstElementOfBatch: The index of the first element in that batch.
 */
export const findBatchContainingElement = (element, totalSize, pageSize) => {
    const totalBatches = Math.ceil(totalSize / pageSize);
    const batchNumber = Math.ceil(element / pageSize);
    const firstElementOfBatch = (batchNumber - 1) * pageSize + 1;

    return { batchNumber, firstElementOfBatch };
};

export const daysOptions = [
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' },
    { label: 'Sunday', value: 'Sunday' },
];

export const hourlyDropdownOptions = [
    { label: '12 AM', value: 1 },
    { label: '1 AM', value: 2 },
    { label: '2 AM', value: 3 },
    { label: '3 AM', value: 4 },
    { label: '4 AM', value: 5 },
    { label: '5 AM', value: 6 },
    { label: '6 AM', value: 7 },
    { label: '7 AM', value: 8 },
    { label: '8 AM', value: 9 },
    { label: '9 AM', value: 10 },
    { label: '10 AM', value: 11 },
    { label: '11 AM', value: 12 },
    { label: '12 PM', value: 13 },
    { label: '1 PM', value: 14 },
    { label: '2 PM', value: 15 },
    { label: '3 PM', value: 16 },
    { label: '4 PM', value: 17 },
    { label: '5 PM', value: 18 },
    { label: '6 PM', value: 19 },
    { label: '7 PM', value: 20 },
    { label: '8 PM', value: 21 },
    { label: '9 PM', value: 22 },
    { label: '10 PM', value: 23 },
    { label: '11 PM', value: 24 },
];

// engagements utilities
export const makeBarDataSeries = (
    data = [],
    isRegionExtended = false,
    isState = false,
    isCity = false,
    isHospital = false,
    isSegment = false,
    isPercentage = false,
) => {
    if (isRegionExtended) {
        return data
            ?.sort((segmentA, segmentB) => segmentB.z - segmentA.z)
            .map((item) => [item?.keyword, item?.z]);
    }
    if (isState) {
        const stateData = {};
        let final_result = [];
        data.forEach((item) => {
            const { region_code, count } = item;
            if (stateData[region_code]) {
                stateData[region_code].count += count;
            } else {
                stateData[region_code] = { ...item };
            }
        });
        if (isPercentage) {
            final_result = Object?.values(stateData)
                ?.sort((a, b) => b?.percentage - a?.percentage)
                ?.map((item) => [
                    capitalizeFirstLetter(
                        item?.region_code + ' [' + item?.engaged_count + ']',
                    ),
                    item?.percentage,
                ]);
        } else {
            final_result = Object?.values(stateData)
                ?.sort((a, b) => b?.count - a?.count)
                ?.map((item) => [
                    capitalizeFirstLetter(item?.region_code),
                    item?.count,
                ]);
            const index = final_result.findIndex((item) =>
                item[0].toLowerCase().includes('unassigned'),
            );

            if (index !== -1) {
                const [unassignedItem] = final_result.splice(index, 1);
                final_result.push(unassignedItem);
            }
        }
        const index = final_result.findIndex((item) =>
            item[0].toLowerCase().includes('unassigned'),
        );

        if (index !== -1) {
            const [unassignedItem] = final_result.splice(index, 1);
            final_result.push(unassignedItem);
        }
        return final_result;
    }
    if (isCity) {
        const cityData = {};
        data.forEach((item) => {
            const { city, state, count } = item;
            const key = `${city}, ${state}`; // Combining city and state as a unique identifier
            if (cityData[key]) {
                cityData[key].count += count;
            } else {
                cityData[key] = { ...item };
            }
        });

        return Object.values(cityData)
            .sort((a, b) => b.count - a.count)
            .map((item) => [capitalizeFirstLetter(item.city), item.count]);
    }
    if (isHospital) {
        const hospitalData = {};

        data.forEach(({ hospital, segment }) => {
            hospital.forEach((entry) => {
                const [hospitalName, count] = Object.entries(entry)[0];
                const category = segment[0];

                if (hospitalData[hospitalName]) {
                    hospitalData[hospitalName].count += count;
                } else {
                    hospitalData[hospitalName] = { count, category };
                }
            });
        });

        return Object.entries(hospitalData)
            .map(([name, { count, category }]) => [name, count, category])
            .sort((a, b) => b[1] - a[1]);
    }
    if (isSegment) {
        const stateData = [];

        data.forEach(({ region_code, count, segment }) => {
            const category = segment[0];
            stateData.push([region_code, count, category]);
        });

        return stateData.sort((a, b) => b[1] - a[1]);
    }
    return [...data]
        .sort((segmentA, segmentB) => segmentB.count - segmentA.count)
        .map((item) => [
            isPercentage && !isState
                ? item?.region + ' ' + item?.percentage + '%'
                : item?.region,
            isPercentage && !isState ? item?.engaged_count : item?.count,
        ]);
};
export const handlePeriodChange = (current, previous) => {
    let period, isPeriodChangeIncrease;
    const periodChange =
        previous === 0 && current === 0
            ? 0
            : ((current - previous) * 100) / previous;
    period = Math.min(100, Math.abs(periodChange).toFixed(0)) + '%';
    if (periodChange < 0) {
        isPeriodChangeIncrease = false;
    } else {
        isPeriodChangeIncrease = true;
    }
    return { period, isPeriodChangeIncrease };
};

export const makeLabels = (
    data,
    categories = false,
    addAllLabel = false,
    isSort = false,
    isDefine = false,
    categoryType = null,
) => {
    let labels = [];

    if (categories) {
        if (Object.entries(data).length !== 0) {
            if (categoryType) {
                labels = Object.keys(data).filter(
                    (key) => data[key][categoryType] > 0,
                );
            } else {
                labels = Object.keys(data?.[Object.keys(data)?.[0]]) || [];
            }

            if (addAllLabel) {
                labels.unshift('All');
            }
        }
    } else {
        for (const category in data) {
            labels.push(category);
        }
    }

    if (isSort) {
        labels.sort();
    }

    if (isDefine) {
        labels = labels.map((label) => ({
            label,
            definition: label === 'All' ? '' : channelDefinitions[label] || '',
        }));
    }

    return labels;
};

export const makeSeriesForDonutChart = (data, key) => {
    const series = [];
    let totalSum = 0;

    if (!key) {
        for (const category in data) {
            if (data[category]) {
                series.push(Number(data[category]));
            }
        }
        return series;
    }

    for (const category in data) {
        totalSum += data[category][key] || 0;
    }
    if (totalSum !== 0) {
        for (const category in data) {
            const value = data[category][key] || 0;
            const percentage =
                value !== 0 && totalSum !== 0 ? (value / totalSum) * 100 : 0;
            series.push(Number(percentage.toFixed(2)));
        }
    }

    return series;
};

export const createDistrictUSAMapData = (data) => {
    const arr = [];

    data?.forEach((item) => {
        if (item?.region?.toLowerCase() !== 'unassigned') {
            arr.push({
                z: item?.count,
                keyword: item?.region,
                lat: item.latitude
                    ? parseFloat(item.latitude.toString())
                    : item.latitude,
                lon: item.longitude
                    ? parseFloat(item.longitude.toString())
                    : item.longitude,
            });
        }
    });

    arr.sort((a, b) => b.z - a.z);
    return arr;
};

export const createStateUSAMapData = (data) => {
    const mapData = [];

    if (data && data.length > 0) {
        const stateData = {};
        data.forEach((item) => {
            const { region_code, count } = item;
            if (stateData[region_code]) {
                stateData[region_code].count += count;
            } else {
                stateData[region_code] = { ...item };
            }
        });
        for (const region_code in stateData) {
            if (stateData.hasOwnProperty(region_code)) {
                const { count } = stateData[region_code];
                if (region_code && count > 0) {
                    mapData.push({
                        value: count,
                        'postal-code': region_code,
                    });
                }
            }
        }
    }

    return mapData;
};

export const getOverallPhysicians = (data, isRezlidhia = false) => {
    if (isRezlidhia) {
        return data?.reduce((acc, current) => acc + current.z, 0);
    }
    return data?.reduce((acc, current) => acc + current.count, 0);
};

export const formatDate = (inputDate) => {
    const parsedDate = parseISO(inputDate);
    const formattedDate = format(parsedDate, "yyyy-MM-dd, h:mm aaaa 'IST'", {
        locale: enUS,
    })?.toUpperCase();

    return formattedDate;
};

export const removeEmptyNestedKeys = (data) => {
    const newData = {};
    const innerLevelKeys = [];
    const keysHasValue = [];

    if (Object.entries(data)?.length === 0) {
        return newData;
    }

    for (const topLevelKey in data) {
        for (const innerLevelKey in data[topLevelKey]) {
            innerLevelKeys.push(innerLevelKey);
        }
        break;
    }

    for (const topLevelKey in data) {
        for (const key of innerLevelKeys) {
            if (data[topLevelKey][key] !== 0) {
                if (!keysHasValue.includes(key)) {
                    keysHasValue.push(key);
                }
            }
        }
    }

    for (const topLevelKey in data) {
        const obj = {};
        for (const key of keysHasValue) {
            obj[key] = data[topLevelKey][key];
        }
        newData[topLevelKey] = obj;
    }

    return newData;
};

export const replaceKeyWithValue = (key) => {
    switch (key) {
        case 'PI':
            return 'Personal Promotion';
        case 'DG':
            return 'Digital Affinity';
        case 'PP':
            return 'Peer to Peer';
        default:
            return key;
    }
};

const generate2DArray = (rows, cols) => {
    const data = [];
    let counter = 1;

    for (let i = 0; i < rows; i++) {
        const row = [];

        for (let j = 0; j < cols; j++) {
            row.push(counter++);
        }

        data.push(row);
    }

    return data;
};

const generateRange = (size, limit) => {
    const ranges = [];

    for (let i = 1; i <= limit; i += size) {
        const start = i;
        const end = Math.min(i + size - 1, limit);
        ranges.push([start, end]);
    }

    return ranges;
};

const findRangeIndex = (range, value) => {
    let start,
        end,
        index = -1;
    const exactValue = Math.ceil(value);
    for (let currentIndex = 0; currentIndex < range.length; currentIndex++) {
        start = range[currentIndex][0];
        end = range[currentIndex][1];

        if (exactValue >= start && exactValue <= end) {
            index = currentIndex;
            break;
        }
    }

    return index;
};

export const createMapSeries = (data) => {
    const mapData = [];
    if (data?.length) {
        data?.map((item) => {
            mapData.push({
                value: item[1],
                'postal-code': item[2],
            });
        });
    }

    return mapData;
};

/* Tooltip text "10%" or, when the API sends HCP counts, "10% (123)" */
export const formatPercentWithCount = (percent, count) => {
    if (count === undefined || count === null) {
        return `${percent}%`;
    }
    return `${percent}% (${Number(count).toLocaleString()})`;
};

export const createDigitalVsNonDigitalData = (data, isAffinityPage = false) => {
    if (!data || data?.length === 0) {
        return { categories: [], seriesData: [] };
    }

    const categories = data?.map((item) => item.preference);
    // HCP counts are only sent for some companies (affinity_count)
    const countsByKey = (key, index) => data?.[index]?.affinity_count?.[key];
    const seriesData = Object.entries(
        data?.reduce((accumulator, item) => {
            Object.entries(item.affinity)?.forEach(([key, value]) => {
                accumulator[key] = [
                    ...(accumulator[key] || []),
                    Number(Math.round(value)),
                ];
            });
            return accumulator;
        }, {}),
    ).map(([name, data]) => ({
        yAxis: name === 'DG' ? 0 : 1,
        name: replaceKeyWithValue(name),
        data,
        counts: data.map((_, index) => countsByKey(name, index)),
        color: isAffinityPage
            ? AFFINITY_HUB_DIGITAL_VS_NON_DIGITAL[name]
            : TARGET_LIST_DIGITAL_VS_NON_DIGITAL[name],
        dataLabels: { style: { color: isAffinityPage ? '#000' : '#FFF' } },
    }));

    return { categories, seriesData };
};

export const createAffinityBySegmentData = (data) => {
    const chartSeries = [];
    const categories = [];
    const affinityData = {};
    const affinityCounts = {};

    if (data?.length > 0) {
        data?.forEach((item) => {
            const category = item.segment;
            categories.push(category);
            item.affinity.forEach((affinity) => {
                let affinityLevel = affinity.level;
                const isNumber = typeof affinityLevel === 'number';
                if (isNumber) {
                    affinityLevel = LEVELS[affinityLevel];
                }
                if (!affinityData[affinityLevel]) {
                    affinityData[affinityLevel] = [];
                }
                affinityData[affinityLevel].push(affinity.percent);
                if (!affinityCounts[affinityLevel]) {
                    affinityCounts[affinityLevel] = [];
                }
                affinityCounts[affinityLevel].push(affinity.count);
            });
        });
        Object.entries(affinityData).forEach(([level, dataValues]) => {
            chartSeries.push({
                name: level,
                data: dataValues,
                counts: affinityCounts[level],
            });
        });
        return { chartSeries, categories };
    }
    return { chartSeries, categories };
};

/* Color Shades for Heat Map */
export const getColor = (type, category) => {
    const colorShades = {
        DG: DG_COLOR_SHADES,
        PI: PI_COLOR_SHADES,
        PP: PP_COLOR_SHADES,
    };

    return colorShades[type]?.[category] || '';
};

/* Generate Chart Range for Affinity Hub Heat Map */
export const generateChartRange = (data, affinityType) => {
    const ranges = [];

    let from = 1;
    let to;

    for (const key in data) {
        to = from + data[key] - 1;

        ranges.push({
            from: Math.round(from),
            to: Math.round(to),
            color: getColor(affinityType, key),
            name: key,
        });

        from = to + 1;
    }

    return ranges;
};

/* Generate Chart Series for Affinity Hub Heat Map */

export const generateChartSeries = (data, affinityType) => {
    const arr = Object.entries(data);
    const allValuesZero = arr.every((item) => item[1] === 0);

    if (allValuesZero) {
        return [];
    }

    const result = generate2DArray(10, 10);
    const range = generateRange(10, 100);

    const chartSeries = result.map((item, index) => ({
        id: index,
        name: '',
        data: item,
    }));

    let cumulativePercentage = 0;

    for (const key in data) {
        const percentage = data[key];

        const index = findRangeIndex(
            range,
            cumulativePercentage + percentage / 2,
        ); // Use midpoint of the range

        if (index > -1) {
            chartSeries[index].name = Math.round(percentage); // Assign the percentage value as the name
        }

        cumulativePercentage += percentage;
    }

    return chartSeries;
};

/* Generate Chart Series for Peer To Peer */
export const createPeerToPeerAffinityData = (
    data = [],
    level = true,
    includeCounts = true,
) => {
    const labels = [];
    const chartSeries = [];
    const counts = [];

    if (level) {
        if (data?.length) {
            for (const upperLayer of data) {
                for (const affinity of upperLayer?.affinity) {
                    if (affinity?.percent > 0) {
                        labels.push(affinity?.level);
                        counts.push(affinity?.count);
                        chartSeries.push(
                            parseFloat(
                                parseFloat(affinity?.percent).toFixed(2),
                            ),
                        ); // Ensure number format
                    }
                }
            }
        }
    } else {
        for (const key in data) {
            const LABEL = getChannelsPartners(key, 'peerToPeer');
            labels.push(LABEL);
            counts.push(data[key]['count']);
            chartSeries.push(
                parseFloat(parseFloat(data[key]['percent'] || 0).toFixed(2)),
            ); // Ensure number format
        }
    }

    return { labels, chartSeries, counts: includeCounts ? counts : [] };
};
/* Generate Chart Series for Topic */
export const createTopicData = (data, includeCounts = true) => {
    const seriesData = [];
    if (data && data?.length) {
        for (const series of data) {
            seriesData.push({
                name: TOPICS[series?.topic || ''],
                y: Number(series?.percent?.toFixed(2)),
                count: includeCounts ? series?.count : undefined,
            });
        }
    }

    return seriesData;
};

/* Generate Chart Series for Content Type */
export const createContentTypeData = (data) => {
    const seriesData = [];

    if (data && data?.length) {
        for (const series of data) {
            seriesData.push({
                name: series?.type,
                y: Number(series?.percent?.toFixed(2)),
            });
        }
    }

    return seriesData;
};

/* Generate Chart Series for Distribution by Years of Experience */
export const createDistributionYearExp = (data) => {
    let chartSeries = [];
    const chartData = [];

    if (data?.data && Array.isArray(data?.data)) {
        data.data.forEach((item) => {
            chartData.push({
                x: item?.experience,
                y: item?.count,
            });
        });
    }

    chartSeries = [{ name: '', data: chartData }];

    return chartSeries;
};

/* Generate Chart Series for Content Affinity Keywords */
export const createTreeMapSeries = (data, seriesName = '', isCount = true) => {
    const seriesData = [];

    data.map((item) => {
        seriesData.push({
            name: item.content,
            value: isCount
                ? Number(item.count.toFixed(2))
                : Number(item.percent.toFixed(2)),
            colorValue: parseInt(item.count ?? 0),
            hcpCount: item.hcp_count,
        });
    });

    return [
        {
            name: seriesName,
            data: seriesData,
        },
    ];
};

/* Create Vendor Prefernce for Vendor Affinity Sankey Chart Series */
export const createVendorPreferences = (data) => {
    const result = [];
    const vendors = [];

    for (const vendor in data) {
        vendors.push(getChannelsPartners(vendor) || vendor);
        const realVendor = getChannelsPartners(vendor) || vendor;
        const vendorPreference = data[vendor];
        vendorPreference.forEach((preference) => {
            const preferenceName = preference.preference;
            const { count } = preference;

            result.push([realVendor, preferenceName, count]);
        });
    }
    return { result, vendors };
};

/* Generate Chart Series for Vendor Affinity */
export const createSankeyChartSeries = (data) => {
    const chartSeries = {};
    const categories = [];

    for (const channel in data) {
        const CHANNEL = getChannelsPartners(channel, 'channel');
        categories.push(CHANNEL);
        const vendorPreference = createVendorPreferences(data[channel]);
        chartSeries[CHANNEL] = vendorPreference;
    }

    return { categories, chartSeries };
};

/* Make Series for Sankey Chart */
export const transformChartSeriesForSankey = (channel = '', data) => {
    const { result, vendors } = data;
    const preferences = [
        {
            id: '1st Preference',
            color: '#2E90FA',
            opposite: true,
        },
        {
            id: '2nd Preference',
            color: '#FDB022',
            opposite: true,
        },
        {
            id: '3rd Preference',
            color: '#12B76A',
            opposite: true,
        },
    ];
    const nodes = vendors.map((vendor, index) => ({
        id: vendor,
        color: colors[index],
    }));
    const series = [
        {
            name: channel,
            keys: ['from', 'to', 'weight'],
            data: result,
            nodes: [...nodes, ...preferences],
            nodePadding: 20,
            dataLabels: {
                useHTML: true,
                nodeFormat:
                    '<div style="padding: 5px;  color: #000; text-align: center;">{point.name}</div>',
            },
        },
    ];

    return { series, vendors };
};

export const customWrapper = (Highcharts) => {
    const H = Highcharts;
    H.wrap(
        H.Chart.prototype,
        'transformToLatLon',
        function (proceed, point, transform) {
            if (!isNaN(point.x) && !isNaN(point.y)) {
                return proceed.apply(
                    this,
                    Array.prototype.slice.call(arguments, 1),
                );
            }
        },
    );
};

/* Generate Chart Series and Categories for Digital and Personal Promotion Channel Affinity Preference */
export const createAffinityChartSeries = (
    data,
    isDigital,
    includeCounts = true,
) => {
    const categories = [];
    const seriesData = [];
    const levels = {
        'Very Low': [],
        Low: [],
        Medium: [],
        High: [],
        'Very High': [],
    };
    const levelCounts = {
        'Very Low': [],
        Low: [],
        Medium: [],
        High: [],
        'Very High': [],
    };

    if (isDigital) {
        for (const channel in data) {
            const CHANNEL = getChannelsPartners(channel, 'channel');
            categories.push(CHANNEL);
            const affinity = data[channel];

            affinity.map((ele) => {
                const { level } = ele;
                const { percent } = ele;

                levels[level].push(percent);
                levelCounts[level].push(ele.count);
            });
        }
    } else {
        Object.keys(data).forEach((category) => {
            const CHANNEL = getChannelsPartners(category, 'channel');
            categories.push(CHANNEL);
            Object.values(data[category]).forEach((ele) => {
                const { level } = ele;
                const { percent } = ele;
                levels[level].push(percent);
                levelCounts[level].push(ele.count);
            });
        });
    }
    for (const level in levels) {
        if (levels[level].length > 0) {
            const obj = {
                name: level,
                data: levels[level],
                counts: includeCounts ? levelCounts[level] : [],
            };

            seriesData.push(obj);
        }
    }
    return { categories, seriesData };
};

/* Generate Chart Series for Digital and Personal Promotion Channel Affinity Preference */
export const generateAffinityChartSeries = (
    data,
    isDigital = true,
    includeCounts = true,
) => {
    const preferences = {};

    for (const preference in data) {
        const channels = data[preference];
        const chartData = createAffinityChartSeries(
            channels,
            isDigital,
            includeCounts,
        );
        preferences[preference] = chartData;
    }
    return preferences;
};

export const calculatePrescriptionPercentage = (
    data = [],
    isPrescriber = true,
) => {
    if (data && data.length) {
        const sortedData = data.sort((a, b) =>
            isPrescriber
                ? b.prescriptions - a.prescriptions
                : b.engagement_count - a.engagement_count,
        );
        const top10Data = sortedData.slice(0, 10);
        const highestPrescriptions = Math.max(
            ...top10Data.map((entry) =>
                isPrescriber ? entry.prescriptions : entry.engagement_count,
            ),
        );

        const result = top10Data.map((entry) => ({
            id: entry.id,
            name: entry.name,
            prescriptions: isPrescriber
                ? entry.prescriptions
                : entry.engagement_count,
            percentage: (
                ((isPrescriber ? entry.prescriptions : entry.engagement_count) /
                    highestPrescriptions) *
                100
            ).toFixed(2),
        }));

        return result;
    }
    return [];
};

// Function to calculate Jaccard similarity coefficient between two strings
export const jaccardSimilarity = (str1, str2) => {
    const set1 = new Set(str1.toLowerCase());
    const set2 = new Set(str2.toLowerCase());

    const intersection = new Set([...set1].filter((x) => set2.has(x)));
    const union = new Set([...set1, ...set2]);

    return intersection.size / union.size;
};

export const getChannelsPartners = (desiredString = '', key = 'partner') => {
    const KEYS = {
        partner: DISTINCT_PARTNERS,
        channel: DISTINCT_CHANNELS,
        peerToPeer: PEER_TO_PEER_AFFINITY,
        engagement: ENGAGEMENT,
        physician_segment: PHYSICIAN_SEGMENT,
    };
    const arr = KEYS[key] || [];
    if (arr.includes(desiredString)) {
        return desiredString;
    }
    // Remove special characters and spaces from desiredString
    desiredString = desiredString?.replace(/[^\w\s]/gi, '').toLowerCase() || '';

    let maxSimilarity = 0;
    let mostSimilarString = null;

    arr?.forEach((item) => {
        const similarity = jaccardSimilarity(
            desiredString,
            item.replace(/[^\w\s]/gi, '').toLowerCase(),
        );
        if (similarity > maxSimilarity) {
            maxSimilarity = similarity;
            mostSimilarString = item;
        }
    });

    return mostSimilarString;
};

export function extractUniqueEngagements(engagements) {
    const sortedEngagements = engagements.sort(
        (a, b) => parseISO(b.action_date) - parseISO(a.action_date),
    );
    const uniqueEngagements = sortedEngagements.reduce(
        (acc, engagement) => {
            acc.channels.push(engagement.engagement_channel);
            acc.vendors.push(engagement.engagement_vendor);

            return acc;
        },
        { channels: [], vendors: [] },
    );

    return {
        channels: Array.from(new Set(uniqueEngagements.channels)),
        vendors: Array.from(new Set(uniqueEngagements.vendors)),
    };
}

export function extractPreferences(data) {
    const channels = [];
    const partners = [];
    for (const npiId in data) {
        if (data.hasOwnProperty(npiId)) {
            const npiData = data[npiId][0]._source;
            if (npiData.partner_preference) {
                partners.push(...npiData.partner_preference);
            }
            if (npiData.channel_preference) {
                channels.push(...npiData.channel_preference);
            }
        }
    }
    return { channels, partners };
}

export const uniqueEngagementsChannelsPartners = (
    engagementData = [],
    extendedEngagementData = {},
) => {
    const obj1 = extractUniqueEngagements(engagementData);
    const obj2 = extractPreferences(extendedEngagementData);

    const commonChannels = obj2.channels.filter((channel) =>
        obj1.channels.includes(channel),
    );

    const commonPartners = obj2.partners.filter((partner) =>
        obj1.vendors.includes(partner),
    );

    return { commonChannels, commonPartners };
};
export const generateChartColorBasisOfChange = (current, previous) => {
    const change =
        previous === 0 && current === 0
            ? 0
            : ((current - previous) * 100) / previous;
    return change > 0
        ? '#32D583'
        : change < 0
          ? '#F97066'
          : change === 0 && '#98A2B3';
};

export const capitalizeFirstLetter = (string) => {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const handleCustomisationForCompanyDashboard = (companyId) => {
    let bool = false;

    switch (companyId) {
        case 18: // Yescarta
            bool = true;
            break;
        case 20: // Tecartus
            bool = true;
            break;
        case 34: // CAR-T HOPE
            bool = true;
            break;
        case 37: // Xevinapant
            bool = true;
            break;
        case 150: // MSL IN QA
            bool = true;
            break;
        case 64: // replimune
            bool = true;
            break;
        case 66: // replimune
            bool = true;
            break;
        case 140: // replimune
            bool = true;
            break;
        case 161: // replimune
            bool = false;
            break;
        default:
            bool = false;
            break;
    }

    return bool;
};
export const handleHideSpecialtyColumns = (companyId) => {
    let isHidden = false;

    switch (companyId) {
        case 3: // Tepmetko
            isHidden = true;
            break;
        case 16: // Bavencio
            isHidden = true;
            break;
        default:
            isHidden = false;
            break;
    }

    return isHidden;
};
export const getLabels = (array = [], key) => {
    if (!key) return [];

    const data = [];
    array.forEach((item) => {
        data.push(item[key]);
    });

    return data;
};

const filteredDataForCreativeNameData = (data) => {
    if (data.length) {
        return data.map((obj) => {
            let sum = 0;
            let count = 0;
            const creativeNameData = {};

            // Calculate sum and count keys
            for (const key in obj.creative_name_data) {
                if (key === '(not set)' || key === '') {
                    sum += obj.creative_name_data[key];
                } else if (obj.creative_name_data[key] !== 0) {
                    count++;
                    creativeNameData[key] = obj.creative_name_data[key];
                }
            }

            // Calculate adjustment value only if there are non-zero keys
            const adjustment =
                count > 0 ? parseFloat((sum / count).toFixed(2)) : 0;

            // Add adjustment value to non-zero key values
            for (const key in creativeNameData) {
                creativeNameData[key] = parseFloat(
                    (parseFloat(creativeNameData[key]) + adjustment).toFixed(2),
                );
            }

            return { month: obj.month, creative_name_data: creativeNameData };
        });
    }
};
export const createChartData = (data = [], key, isPrimaryColor = false) => {
    if (data && data.length) {
        const sorted_data = [...data].sort((a, b) => {
            const dateA = new Date(a.month + ' 1');
            const dateB = new Date(b.month + ' 1');
            return dateA - dateB;
        });

        const labels = getLabels(sorted_data, 'month');
        const keys = ['sessions_value', 'quality_visits_value', 'count'];
        const bool = keys.includes(key);
        let dataSets = [],
            allValuesZero;

        if (bool) {
            dataSets = sorted_data.map((item, index) => ({
                name: item['month'],
                y: item[key],
                color: isPrimaryColor ? '#AD509E' : colors[index],
            }));

            allValuesZero = dataSets.every((item) => item.y === 0);
            if (allValuesZero) dataSets = [];
        } else {
            const tempArray = {};
            let COLOR_COUNT = 0;

            sorted_data.forEach((item) => {
                const newItemArray = Object.keys(item[key]);

                if (COLOR_COUNT < newItemArray.length) {
                    COLOR_COUNT = newItemArray.length;
                }

                newItemArray.forEach((name) => {
                    if (!tempArray[name]) tempArray[name] = [];
                    tempArray[name].push(item[key][name]);
                });
            });

            const tempItemArray = Object.keys(tempArray);
            tempItemArray.forEach((key, index) => {
                dataSets.push({
                    name: key,
                    data: tempArray[key],
                    fillColor: colors[index],
                });
            });

            allValuesZero = dataSets.every((item) =>
                item.data.every((value) => value === 0),
            );
            if (allValuesZero) dataSets = [];
        }

        return {
            xAxisCategories: labels,
            dataSets,
            colors,
        };
    }
    return [];
};

// Function to get the month's index
export const getMonthIndex = (month) =>
    [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ].indexOf(month);

// Sort the data based on the month and year
export const sortDataWithMonthYear = (data = []) =>
    data.sort((a, b) => {
        const [aMonth, aYear] = a.split(' ');
        const [bMonth, bYear] = b.split(' ');

        // Compare years first
        if (aYear !== bYear) {
            return aYear - bYear;
        }

        // If years are the same, compare months
        return getMonthIndex(aMonth) - getMonthIndex(bMonth);
    });

export const removeNotFound = (data = []) => {
    if (data && data.length > 0) {
        return data.filter((item) => {
            if (item[0].includes('NOT FOUND')) {
                return;
            }
            return item;
        });
    }

    return [];
};

export const validateForScriptTag = (value) => {
    if (typeof value === 'string' && value.toLowerCase().includes('<script>')) {
        return false;
    }
    return true;
};
export const sortedDataBasedValue = (physicians, value) => {
    if (physicians) {
        return physicians.sort((a, b) => {
            if (
                a.suggest_fmt_trigger === value &&
                b.suggest_fmt_trigger !== value
            ) {
                return -1;
            } else if (
                a.suggest_fmt_trigger !== value &&
                b.suggest_fmt_trigger === value
            ) {
                return 1;
            } else {
                return 0;
            }
        });
    }
};
export const updateStringNPPA = (inputString) => {
    const regex = /\b(NPPAs?|Oncs|ONCS|DLBCL\s*-\s*NPPAs?|FL\s*-\s*NPPAs?)\b/gi;
    return inputString.replace(regex, (match) => {
        const lowerMatch = match.toLowerCase();

        if (lowerMatch.includes('nppa')) {
            if (lowerMatch.includes('dlbcl')) {
                return match.replace(/nppas?/i, 'NP/PAs');
            } else if (lowerMatch.includes('fl')) {
                return match.replace(/nppas?/i, 'NP/PAs');
            }
            return match.replace(/nppas?/i, 'NP/PAs');
        } else if (lowerMatch.includes('oncs')) {
            return match.replace(/oncs/i, 'Oncologists');
        }
        return match;
    });
};

/**
 * Extracts the bucket name from an AWS S3 URL.
 *
 * @param {string} url - The URL of an object stored in an AWS S3 bucket.
 * @returns {string} The extracted bucket name.
 */
export const fetchBucketName = (url) => {
    const parts = url.split('/');
    const bucketName = parts[2].split('.')[0];

    return bucketName;
};

/**
 * Extracts the file name from an AWS S3 URL.
 *
 * @param {string} url - The URL of an object stored in an AWS S3 bucket.
 * @returns {string} The extracted file name.
 */
export const fetchFileName = (url) => {
    const fileName = url.split('/').pop();

    return fileName;
};

// for sorting column
export const getSortedData = (orderBy, order, data) => {
    if (orderBy === null) {
        return data;
    }
    if (data) {
        return [...data].sort((a, b) => {
            let valueA = getValueByNestedProperty(a, orderBy);
            let valueB = getValueByNestedProperty(b, orderBy);
            valueA = Array.isArray(valueA)
                ? valueA
                : /^-?\d*\.?\d+$/.test(valueA)
                  ? Number(valueA)
                  : valueA;
            valueB = Array.isArray(valueB)
                ? valueB
                : /^-?\d*\.?\d+$/.test(valueB)
                  ? Number(valueB)
                  : valueB;
            if (Array.isArray(valueA) && Array.isArray(valueB)) {
                const minLength = Math.min(valueA.length, valueB.length);
                for (let i = 0; i < minLength; i++) {
                    const comparison = valueA[i].localeCompare(valueB[i]);
                    if (comparison !== 0) {
                        return order === 'asc' ? comparison : -comparison;
                    }
                }
                return order === 'asc'
                    ? valueA.length - valueB.length
                    : valueB.length - valueA.length;
            }

            const comparison =
                typeof valueA === 'number' && typeof valueB === 'number'
                    ? valueA - valueB
                    : String(valueA).localeCompare(String(valueB));

            return order === 'asc' ? comparison : -comparison;
        });
    }
};
export const getValueByNestedProperty = (object, path) => {
    const keys = path.split('.');
    let value = object;
    for (const key of keys) {
        if (!value.hasOwnProperty(key)) return null;
        value = value[key];
    }
    return value;
};
export const calculateYmax = (series, type) => {
    let max = 10;
    if (series.length) {
        if (type === 'All') {
            for (let i = 0; i < series[0].data.length; i++) {
                const sum = series.reduce(
                    (acc, curr) => acc + (curr.data[i] > 0 ? curr.data[i] : 0),
                    0,
                );
                max = Math.max(max, sum);
            }
        } else {
            max = Math.max(
                ...series.flatMap((item) => item.data.map((data) => data.y)),
            );
        }
        let add = Math.ceil(max / 10) * 10 * 0.05;
        add = Math.min(add, 500);
        max = Math.ceil(Math.ceil(max / 10) * 10 + add);

        if (max % 5 !== 0) {
            max += 5 - (max % 5);
        }

        return max;
    }
    return max;
};

export const setTickAmount = (data) => {
    if (data % 4 === 0) {
        return 4;
    }
    return 5;
};

export const convertedTriggerPhysiciansData = (data) => {
    if (data) {
        return data.map((item) => ({
            ...item,
            suggest_fmt_trigger:
                item.digital_engagement_priority === 'High' &&
                item.suggest_fmt_trigger === 'None'
                    ? 'Recommended'
                    : item.suggest_fmt_trigger,
        }));
    }
};
export const handleHideWebMetricsButton = (
    companyId,
    isOmaniChannel = false,
) => {
    if (companyId && !isOmaniChannel) {
        const dontShowToCompanyIds = [
            33, 34, 32, 18, 20, 42, 45, 3, 54, 58, 62, 38, 68, 97, 112,
        ];
        return !dontShowToCompanyIds.includes(companyId);
    }
    return false;
};

export const handleHidePaidSearchButton = (
    companyId,
    isOmaniChannel = false,
) => {
    if (companyId && !isOmaniChannel) {
        if (companyId === 112) return false;
        const dontShowToCompanyIds = [66, 233, 150, 71, 67, 91, 98, 99, 15, 16];
        return dontShowToCompanyIds.includes(companyId) || companyId >= 100;
    }
    return false;
};

export const handleShowAlwaysOnButton = (companyId) => {
    if (companyId) {
        const companyIds = [18, 20];
        return companyIds.includes(companyId);
    }
    return false;
};
export const handleRowClassForEngagementsTable = (index, companyId) => {
    let classes = '';
    if (companyId === REGENERON_COMPANY_ID && index % 2 === 0) {
        classes = 'odd-row-background cursor-pointer';
    } else if (index % 2 === 0) {
        classes = 'odd-row-background';
    }
    return classes;
};
export function convertFullMonthNamesToShort(categories, months) {
    const monthMap = {
        January: 'Jan',
        February: 'Feb',
        March: 'Mar',
        April: 'Apr',
        May: 'May',
        June: 'Jun',
        July: 'Jul',
        August: 'Aug',
        September: 'Sep',
        October: 'Oct',
        November: 'Nov',
        December: 'Dec',
    };
    if (months) {
        let [month, year] = months?.split(' ');
        month = monthMap[month];
        return `${month} ${year}`;
    } else {
        return categories?.map((category) => {
            let [month, year] = category?.split(' ');
            month = monthMap[month];
            return `${month} ${year}`;
        });
    }
}
export const calculateMaxWidth = () => {
    let width = '100%';
    const deviceWidth = window.innerWidth;
    if (deviceWidth <= 1400 && deviceWidth >= 1300) {
        width = '1050px';
    } else if (deviceWidth < 1300 && deviceWidth >= 1200) {
        width = '970px';
    } else if (deviceWidth === 1440) {
        width = '1130px';
    } else if (deviceWidth === 1600) {
        width = '1310px';
    }
    return width;
};

export function sortDataByMonth(data) {
    if (!Array.isArray(data)) {
        console.error('Invalid input: data should be an array');
        return [];
    }

    return data.sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);

        if (isNaN(dateA) || isNaN(dateB)) {
            console.error('Invalid date format in month property');
            return 0;
        }

        return dateA - dateB;
    });
}

export const downloadFiles = (files) => {
    let index = 0;
    const downloadNext = () => {
        if (index >= files.length) {
            return;
        }

        const { file_url, file_name } = files[index];
        if (file_url && file_name) {
            const a = document.createElement('a');
            a.href = file_url;
            a.download = file_name;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }

        index++;
        setTimeout(downloadNext, 500);
    };

    downloadNext();
};

export const processDataForClickEngagement = (selectedChannel, channelData) => {
    let data = [];

    if (selectedChannel?.toLowerCase() === 'all') {
        let channelValues = Object?.entries(channelData)?.map(
            ([channel, value]) => ({
                name: channel,
                value: Object?.values(value)?.reduce(
                    (acc, val) => acc + val,
                    0,
                ),
            }),
        );
        channelValues = channelValues?.filter((channel) => channel.value !== 0);

        channelValues?.sort((a, b) => b.value - a.value);

        data = channelValues?.map((channel) => ({
            name: channel.name,
            data: Object?.values(channelData[channel.name]),
        }));

        return data;
    } else {
        data = Object?.entries(channelData)?.map((item, index) => {
            const [channel, value] = item;
            return {
                x: channel,
                y: value[selectedChannel] || 0,
                fillColor: colors[index],
            };
        });
        return [
            {
                name: selectedChannel,
                data: data,
            },
        ];
    }
};
export const makephysicianEngagementSegmentsDonutChartData = (
    data,
    isSort = false,
) => {
    if (isSort) {
        if (Object.entries(data).length >= 1) {
            const sortedKeys = Object.keys(data).sort();

            const sortedObject = {};
            sortedKeys.forEach((key) => {
                sortedObject[key] = data[key];
            });

            return Object.entries(sortedObject)?.map(([key, value]) => ({
                name: key,
                y: parseFloat(value),
            }));
        }
        return [];
    }
    return Object.entries(data)?.map(([key, value]) => ({
        name: key,
        y: parseFloat(value),
    }));
};

export const customCategories = (categories = [], isLong = false) => {
    let finalCategories = [];

    if (categories?.length > 0) {
        if (isLong) {
            finalCategories = categories.map((dateStr) => {
                const [month, year] = dateStr.split(' ');
                const shortMonth = new Date(`${month} 15, 2000`).toLocaleString(
                    'default',
                    { month: 'short' },
                );
                return `${shortMonth} ${year}`;
            });
        } else {
            finalCategories = categories.map((dateStr) => {
                const [year, month] = dateStr.split('-');
                const date = new Date(`${year}-${month}-15`);
                const shortMonth = date.toLocaleString('default', {
                    month: 'short',
                });
                return `${shortMonth} ${year}`;
            });
        }
    }

    return finalCategories;
};

export const prepareTopCitySiteChartData = (data, type) => {
    if (!data || !data[type] || !Array.isArray(data[type].click)) {
        return {
            series: [{ name: 'Clicks', data: [] }],
            categories: [],
        };
    }

    const clicks = data[type].click.filter(
        (item) =>
            item[type === 'cities' ? 'city' : 'site'] &&
            item[type === 'cities' ? 'city' : 'site'].trim() !== '' &&
            item[type === 'cities' ? 'city' : 'site'].toLowerCase() !==
                'unknown',
    );

    const impressions = (data[type].impressions || []).filter(
        (item) =>
            item[type === 'cities' ? 'city' : 'site'] &&
            item[type === 'cities' ? 'city' : 'site'].trim() !== '' &&
            item[type === 'cities' ? 'city' : 'site'].toLowerCase() !==
                'unknown',
    );

    const topClicks = clicks.sort((a, b) => b.count - a.count).slice(0, 10);

    const topImpressions =
        impressions.length > 0
            ? impressions.filter((item) =>
                  topClicks.some(
                      (click) =>
                          click[type === 'cities' ? 'city' : 'site'] ===
                          item[type === 'cities' ? 'city' : 'site'],
                  ),
              )
            : Array(topClicks.length).fill({ count: 0 });

    const cleanName = (name) =>
        name.replace(/https?:\/\/(www\.)?|www\./g, '').replace(/\/$/, '');

    const categories = topClicks.map((item) =>
        cleanName(item[type === 'cities' ? 'city' : 'site'] || 'Unknown'),
    );
    const clickData = topClicks.map((item) => item.count || 0);
    const impressionData = topImpressions.map((item) => item.count || 0);

    return {
        series: [{ name: 'Clicks', data: clickData }],
        categories,
    };
};
export const transformHospitalDataForMapChart = (data, filterType = 'all') => {
    const stateData = {};

    data.forEach(({ region_code, count, hospital }) => {
        if (!stateData[region_code]) {
            stateData[region_code] = {
                value: 0,
                'postal-code': region_code,
                hospital: {},
            };
        }

        let hospitalValueSum = 0;

        hospital.forEach(({ name, count, segment }) => {
            const lowerSegments = Array.isArray(segment)
                ? segment.map((seg) => seg.toLowerCase())
                : (segment || '').toLowerCase();

            if (
                (filterType === 'collaborators' &&
                    !lowerSegments.includes('collaborators')) ||
                (filterType === 'non-collaborators' &&
                    !lowerSegments.includes('non-collaborators'))
            ) {
                return;
            }

            if (!stateData[region_code].hospital[name]) {
                stateData[region_code].hospital[name] = 0;
            }

            stateData[region_code].hospital[name] += count;
            hospitalValueSum += count;
        });

        stateData[region_code].value += hospitalValueSum;
    });

    return Object.values(stateData)
        .filter(({ value }) => value > 0)
        .map(({ value, 'postal-code': postalCode, hospital }) => ({
            value,
            'postal-code': postalCode,
            hospital: Object.entries(hospital)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({ [name]: count })),
        }));
};

export const transformHospitalDataForBarChart = (
    data,
    isState = false,
    isHospital = false,
    filterType = 'all',
) => {
    const resultData = {};

    data.forEach(({ region_code, hospital }) => {
        hospital.forEach(({ name, count, segment }) => {
            const lowerSegments = Array.isArray(segment)
                ? segment.map((seg) => seg.toLowerCase())
                : [(segment || '').toLowerCase()];

            if (
                (filterType === 'collaborators' &&
                    !lowerSegments.includes('collaborators')) ||
                (filterType === 'non-collaborators' &&
                    !lowerSegments.includes('non-collaborators'))
            ) {
                return;
            }

            if (isState) {
                if (!resultData[region_code]) {
                    resultData[region_code] = 0;
                }
                resultData[region_code] += count;
            }

            if (isHospital) {
                if (!resultData[name]) {
                    resultData[name] = 0;
                }
                resultData[name] += count;
            }
        });
    });

    return Object.entries(resultData)
        .map(([key, count]) => [key, count, filterType])
        .sort((a, b) => b[1] - a[1]);
};

export function convertYYYYMMToShortMonth(yyyyMM) {
    const monthMap = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];

    const [year, month] = yyyyMM.split('-');
    const shortMonth = monthMap[parseInt(month, 10) - 1];
    return `${shortMonth} ${year}`;
}

export function formatDateForExport(date) {
    if (!date) return '';
    const d = new Date(date);
    const formatted = `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
    return formatted.toString();
}
